import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AnimatePresence, motion } from "@/lib/motion";
import { useDispatch } from "react-redux";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UsersAPI } from "../api/usersApi";
import { useGetUserDetails } from "../hooks/useGetUserDetails";
import { updateForeignNationalAvatar } from "../redux/foreignNational/foreignNationalSlice";
import { updateIndividualAvatar } from "../redux/individual/individualSlice";
import { updateLawyerAvatar } from "../redux/lawyer/lawyerSlice";
import { normalizeAvatarSrc } from "../helpers/userDisplay";
import { UserMenuItems } from "./UserMenuItems";
import { SettingsModal } from "./settings/SettingsModal";

/**
 * Header (legacy pages) avatar dropdown. The app sidebar uses
 * `components/app-sidebar/NavUser` instead.
 */
export const UserMenu = () => {
  const {
    userDetails: { userFullName, avatarSrc, id: uid, userType }
  } = useGetUserDetails();

  const dispatch = useDispatch();

  const normalizedAvatarSrc = useMemo(
    () => normalizeAvatarSrc(avatarSrc),
    [avatarSrc]
  );

  const initials = useMemo((): string => {
    const parts = userFullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ""}${
      parts[parts.length - 1][0] ?? ""
    }`.toUpperCase();
  }, [userFullName]);

  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = () => setIsOpen((v) => !v);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
    setIsOpen(false);
  }, []);

  const avatarFetchAttemptByUidRef = useRef<Record<string, number>>({});
  const retryTimerRef = useRef<number | null>(null);
  const [avatarFetchRetrySeq, setAvatarFetchRetrySeq] = useState(0);

  useEffect(() => {
    const id = (uid ?? "").trim();
    if (!id) return () => {};

    // If we already have an avatar image, don't fetch.
    if (normalizedAvatarSrc) return () => {};

    const attempts = avatarFetchAttemptByUidRef.current[id] ?? 0;
    if (attempts >= 3) return () => {};
    avatarFetchAttemptByUidRef.current[id] = attempts + 1;

    let cancelled = false;
    (async () => {
      try {
        const profile = await UsersAPI.getProfile(id);
        const url = String(profile?.avatar_data_url ?? "").trim();
        if (!url || cancelled) return;

        if (userType === "Lawyer") {
          dispatch(updateLawyerAvatar({ avatarSrc: url, avatarName: "" }));
        } else if (userType === "Individual") {
          dispatch(updateIndividualAvatar({ avatarSrc: url, avatarName: "" }));
        } else if (userType === "ForeignNational") {
          dispatch(
            updateForeignNationalAvatar({ avatarSrc: url, avatarName: "" })
          );
        }
      } catch {
        // Best-effort preload; retry a couple times (auth token can be late).
        if (cancelled) return;
        if ((avatarFetchAttemptByUidRef.current[id] ?? 0) < 3) {
          if (retryTimerRef.current) {
            window.clearTimeout(retryTimerRef.current);
          }
          retryTimerRef.current = window.setTimeout(() => {
            setAvatarFetchRetrySeq((v) => v + 1);
          }, 2000);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [uid, userType, normalizedAvatarSrc, dispatch, avatarFetchRetrySeq]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const wrapper = wrapperRef.current;
      if (wrapper && !wrapper.contains(event.target as Node)) close();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen, close]);

  const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    top: "50px",
    right: "8px",
    zIndex: 1400
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="flex items-center">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          className="flex items-center justify-center rounded-lg outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40"
          onClick={toggle}
        >
          <Avatar size="default">
            {normalizedAvatarSrc ? (
              <AvatarImage src={normalizedAvatarSrc} alt="" />
            ) : null}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </div>
      <AnimatePresence>
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            style={dropdownStyle}
          >
            <UserMenuItems
              onRequestClose={close}
              onOpenSettings={openSettings}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <SettingsModal open={isSettingsOpen} onOpenChange={setIsSettingsOpen} />
    </div>
  );
};
